import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import StatusPill from '../../components/StatusPill';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

type Lead = {
  id: string; company_name: string; fantasy_name: string | null; city: string | null;
  current_carrier: string | null; monthly_spend: string | null; line_count: number | null;
  status: string; score: number | null; in_claro_base: boolean | null; origin: string; assigned_to: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  novo: 'Novo', em_contato: 'Em contato', proposta_enviada: 'Proposta enviada',
  em_negociacao: 'Em negociação', fechado_ganho: 'Ganho', fechado_perdido: 'Perdido',
  descartado: 'Descartado',
};

export const fmtMoney = (v: string | number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get('status') ?? '');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page) });
      if (q) p.set('q', q);
      if (status) p.set('status', status);
      const d = await api.get<{ leads: Lead[]; total: number }>(`/leads?${p}`);
      setLeads(d.leads);
      setTotal(d.total);
    } finally {
      setLoading(false);
    }
  }, [page, q, status]);

  useEffect(() => { load(); }, [load]);

  const [sellers, setSellers] = useState<{ id: string; fullName: string }[]>([]);
  useEffect(() => {
    if (user?.role === 'vendedor') return;
    api.get<{ users: { id: string; fullName: string; role: string; isActive: boolean }[] }>('/users')
      .then((d) => setSellers(d.users.filter((x) => x.role === 'vendedor' && x.isActive)))
      .catch(() => setSellers([]));
  }, [user]);

  async function direcionar(id: string, sellerId: string) {
    try {
      await api.patch(`/leads/${id}`, { assigned_to: sellerId });
      toast.success(`Direcionado: ${sellers.find((s) => s.id === sellerId)?.fullName ?? 'vendedor'}.`);
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha');
    }
  }

  const pages = Math.max(1, Math.ceil(total / 50));
  return (
    <div>
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Funil</p>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">Leads ({total.toLocaleString('pt-BR')})</h1>
        </div>
        <input
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          placeholder="Buscar empresa, CNPJ ou telefone"
          className="ml-auto h-9 w-64 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm"
        />
        <select
          value={status}
          onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          className="h-9 rounded-lg border border-[var(--border-strong)] bg-white px-2 text-sm"
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </header>
      <div className="mt-4 rounded-lg border border-[var(--border)] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] text-left text-xs text-[var(--text-dim)] uppercase tracking-wider">
              <th className="px-4 py-3 font-medium">Empresa</th>
              <th className="px-4 py-3 font-medium">Cidade</th>
              <th className="px-4 py-3 font-medium">Operadora atual</th>
              <th className="px-4 py-3 text-right font-medium">Gasto/mês</th>
              <th className="px-4 py-3 text-right font-medium">Linhas</th>
              <th className="px-4 py-3 font-medium">Status</th>
              {user?.role !== 'vendedor' && <th className="px-4 py-3 font-medium">Vendedor</th>}
            </tr>
          </thead>
          <tbody>
            {loading && [0, 1, 2, 3, 4].map((i) => (
              <tr key={i} className="border-b border-[var(--border)]">
                <td className="px-4 py-3"><span className="block h-3.5 w-40 animate-pulse rounded bg-zinc-100" /></td>
                <td className="px-4 py-3"><span className="block h-3.5 w-20 animate-pulse rounded bg-zinc-100" /></td>
                <td className="px-4 py-3"><span className="block h-3.5 w-14 animate-pulse rounded bg-zinc-100" /></td>
                <td className="px-4 py-3"><span className="ml-auto block h-3.5 w-16 rounded bg-zinc-100" /></td>
                <td className="px-4 py-3"><span className="ml-auto block h-3.5 w-10 rounded bg-zinc-100" /></td>
                <td className="px-4 py-3"><span className="block h-3.5 w-20 rounded bg-zinc-100" /></td>
              </tr>
            ))}
            {!loading && leads.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-[var(--text-dim)]">
                  Nenhum lead com esse filtro. Importe um batch da Claro para começar o funil.
                </td>
              </tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-subtle)]">
                <td className="px-4 py-3">
                  <Link to={`/painel/leads/${l.id}`} className="font-medium hover:underline">{l.company_name}</Link>
                  {l.in_claro_base && <span className="ml-2 rounded bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-dim)]">Na base Claro</span>}
                </td>
                <td className="px-4 py-3 text-[var(--text-dim)]">{l.city ?? '—'}</td>
                <td className="px-4 py-3 text-[var(--text-dim)]">{l.current_carrier ?? '—'}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(l.monthly_spend)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{l.line_count ?? '—'}</td>
                <td className="px-4 py-3"><StatusPill status={l.status} /></td>
                {user?.role !== 'vendedor' && (
                  <td className="px-4 py-3">
                    <select
                      value={''}  /* controlado por ação: evita loop de estado; escolha = PATCH */
                      onChange={(e) => { void direcionar(l.id, e.target.value); e.currentTarget.selectedIndex = 0; }}
                      className="h-8 rounded border border-[var(--border-strong)] bg-white px-1 text-xs"
                    >
                      <option value="">Direcionar...</option>
                      {sellers.map((s) => <option key={s.id} value={s.id}>{s.fullName}</option>)}
                    </select>
                    {l.assigned_to ? (
                      <span className="ml-1 text-[10px] text-[var(--text-dim)]">
                        {sellers.find((s) => s.id === l.assigned_to)?.fullName ?? 'ok'}
                      </span>
                    ) : null}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-2 text-sm">
        <button
          disabled={page <= 1 || loading}
          onClick={() => setPage(page - 1)}
          className="h-8 rounded-md border border-[var(--border-strong)] px-3 disabled:opacity-40 hover:bg-[var(--bg-subtle)]"
        >
          Anterior
        </button>
        <span className="text-[var(--text-dim)]">Página {page} de {pages}</span>
        <button
          disabled={page >= pages || loading}
          onClick={() => setPage(page + 1)}
          className="h-8 rounded-md border border-[var(--border-strong)] px-3 disabled:opacity-40 hover:bg-[var(--bg-subtle)]"
        >
          Próxima
        </button>
      </div>
    </div>
  );
}
